import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { FadeUp } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5 text-amber-400">
    {[...Array(Math.round(n))].map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-current" />
    ))}
  </div>
);

export default function GoogleReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return <div data-testid="reviews-loading" className="h-48 animate-pulse rounded-2xl bg-navy-surface" />;
  }

  const live = data.source === "google";

  return (
    <div data-testid="google-reviews-widget">
      <FadeUp className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">
            {live ? "Live from Google" : "Word on the street"}
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Real reviews from real moves.
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-display text-4xl font-black text-white">{Number(data.rating).toFixed(1)}</span>
          <div>
            <Stars n={data.rating} />
            <p className="mt-1 text-xs text-slate-400">
              {live ? `${data.user_ratings_total} Google reviews` : "Verified customer reviews"}
            </p>
          </div>
        </div>
      </FadeUp>

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {data.reviews.map((r, i) => (
          <FadeUp key={`${r.author_name}-${i}`} delay={i * 0.07}
            className="flex flex-col rounded-2xl border border-white/10 bg-navy-surface p-8">
            <div className="flex items-center gap-3">
              {r.author_photo_uri ? (
                <img src={r.author_photo_uri} alt="" className="h-10 w-10 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 font-display text-sm font-black text-navy">
                  {r.author_name.slice(0, 1)}
                </span>
              )}
              <div>
                {r.author_uri ? (
                  <a href={r.author_uri} target="_blank" rel="noreferrer"
                    data-testid={`review-author-${i}`}
                    className="text-sm font-bold text-white underline decoration-amber-500/50">
                    {r.author_name}
                  </a>
                ) : (
                  <p className="text-sm font-bold text-white" data-testid={`review-author-${i}`}>{r.author_name}</p>
                )}
                <Stars n={r.rating} />
              </div>
            </div>
            <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">"{r.text}"</p>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <span>{r.published || (live ? "" : "Verified Google review")}</span>
              {r.maps_uri && (
                <a href={r.maps_uri} target="_blank" rel="noreferrer" data-testid={`review-maps-link-${i}`}
                  className="inline-flex items-center gap-1 text-amber-500/90 hover:text-amber-400">
                  View on Google Maps <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </FadeUp>
        ))}
      </div>

      {live && data.maps_uri && (
        <FadeUp delay={0.2} className="mt-8 text-center">
          <a href={data.maps_uri} target="_blank" rel="noreferrer" data-testid="reviews-google-link"
            className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300">
            See all reviews on Google <ExternalLink className="h-4 w-4" />
          </a>
        </FadeUp>
      )}
    </div>
  );
}
